import React, { useState, useEffect } from 'react';
import {
  TRANSMISSION_CONFIG,
  MODE_WORDING,
  PRUDENCE_NOTICE,
  DASHBOARD_DISCLAIMER,
} from '../config/transmissionConfig';
import { getDistinctCategories } from '../services/vaultService';
import GlowCard from './GlowCard';

const G = '#8892A4';
const S = '#00F5A0';
const AMBER = '#F59E0B';

const delayToText = (delay) => {
  const n = parseInt(delay.replace('J+', ''), 10);
  return isNaN(n) ? delay : `sous ${n} jours`;
};

const LockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1.5" y="6" width="11" height="7" rx="2"/>
    <path d="M4 6V4.5a3 3 0 016 0V6"/>
  </svg>
);

const TransmissionRecap = ({ beneficiaries, vaultLocked }) => {
  const [vaultCats, setVaultCats] = useState([]);
  const [loadingState, setLoadingState] = useState('loading');

  useEffect(() => {
    getDistinctCategories().then((result) => {
      if (result.success) {
        setVaultCats(result.data);
        setLoadingState('ready');
      } else {
        setLoadingState('error');
      }
    });
  }, []);

  const hasAnyAttribution = beneficiaries.some(
    (b) => (b.access_categories || []).length > 0
  );

  const allAttributedCats = new Set(
    beneficiaries.flatMap((b) => b.access_categories || [])
  );

  const unattributedCats =
    loadingState === 'ready'
      ? vaultCats.filter((cat) => !allAttributedCats.has(cat))
      : [];

  const showUnattributedAlert =
    !vaultLocked && hasAnyAttribution && unattributedCats.length > 0;

  const sortedBeneficiaries = [...beneficiaries]
    .sort((a, b) => (a.priority || 1) - (b.priority || 1))
    .filter((b) => (b.access_categories || []).length > 0);

  return (
    <GlowCard style={{ marginBottom: 28 }}>
      <h2
        style={{
          fontSize: 12,
          fontWeight: 600,
          margin: '0 0 20px',
          color: 'rgba(255,255,255,0.35)',
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
        }}
      >
        En cas de transmission, voici ce qui est prévu
      </h2>

      {loadingState === 'loading' && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            color: G,
            fontSize: 13,
            padding: '4px 0',
          }}
        >
          <div
            style={{
              width: 14,
              height: 14,
              border: '2px solid rgba(255,255,255,0.1)',
              borderTopColor: S,
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
              flexShrink: 0,
            }}
          />
          Chargement...
        </div>
      )}

      {loadingState === 'error' && (
        <div
          style={{
            background: 'rgba(245,158,11,0.08)',
            border: '1px solid rgba(245,158,11,0.2)',
            borderRadius: 10,
            padding: '12px 16px',
            fontSize: 13,
            color: AMBER,
            lineHeight: 1.6,
          }}
        >
          Impossible de charger les informations du coffre.
        </div>
      )}

      {loadingState === 'ready' && vaultLocked && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            color: G,
            fontSize: 13,
            padding: '4px 0',
          }}
        >
          <span style={{ color: 'rgba(255,255,255,0.2)', flexShrink: 0 }}>
            <LockIcon />
          </span>
          Déverrouillez votre coffre pour afficher le récap de transmission.
        </div>
      )}

      {loadingState === 'ready' && !vaultLocked && !hasAnyAttribution && (
        <p style={{ margin: 0, fontSize: 13, color: G, lineHeight: 1.6 }}>
          Vous n'avez pas encore défini qui reçoit quoi. Configurez vos
          bénéficiaires pour organiser la transmission.
        </p>
      )}

      {loadingState === 'ready' && !vaultLocked && hasAnyAttribution && (
        <>
          {showUnattributedAlert && (
            <div
              style={{
                background: 'rgba(245,158,11,0.08)',
                border: '1px solid rgba(245,158,11,0.25)',
                borderRadius: 10,
                padding: '10px 14px',
                fontSize: 13,
                color: AMBER,
                marginBottom: 20,
                lineHeight: 1.6,
              }}
            >
              Certains éléments ne sont pas encore attribués à un bénéficiaire.
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {sortedBeneficiaries.map((b) => (
              <div key={b.id}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: '#fff',
                    marginBottom: 8,
                  }}
                >
                  {b.name} (Priorité {b.priority})
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {(b.access_categories || []).map((cat) => {
                    const cfg = TRANSMISSION_CONFIG[cat];
                    if (!cfg) {
                      console.warn(
                        `[SafeHeir] TransmissionRecap: catégorie inconnue "${cat}", ignorée.`
                      );
                      return null;
                    }
                    return (
                      <div
                        key={cat}
                        style={{
                          display: 'flex',
                          alignItems: 'baseline',
                          gap: 6,
                          fontSize: 13,
                          lineHeight: 1.6,
                        }}
                      >
                        <span
                          style={{
                            color: 'rgba(255,255,255,0.2)',
                            flexShrink: 0,
                            userSelect: 'none',
                          }}
                        >
                          —
                        </span>
                        <span>
                          <span style={{ color: '#fff', fontWeight: 500 }}>
                            {cfg.label}
                          </span>
                          <span style={{ color: G }}>
                            {' '}— {MODE_WORDING[cfg.mode]} ({delayToText(cfg.delay)})
                          </span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div
            style={{
              marginTop: 24,
              paddingTop: 16,
              borderTop: '1px solid rgba(255,255,255,0.06)',
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: 11,
                color: 'rgba(255,255,255,0.3)',
                lineHeight: 1.7,
              }}
            >
              {PRUDENCE_NOTICE}
            </p>
            <p
              style={{
                margin: 0,
                fontSize: 11,
                color: 'rgba(255,255,255,0.3)',
                lineHeight: 1.7,
              }}
            >
              {DASHBOARD_DISCLAIMER}
            </p>
          </div>
        </>
      )}
    </GlowCard>
  );
};

export default TransmissionRecap;
